import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Info from '@mui/icons-material/Info';
import {
  ClickAwayListener,
  Collapse,
  Grid,
  Grow,
  IconButton,
  List,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import React, { memo, startTransition, useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { ClickEvent } from '../../../../common-types';
import { dashboardConfigurations } from '../../dashboard/dashboard-configurations';
import { teal2 } from '../../theme/color-constants';
import { Dashboards } from '../../v2/common/components/dashboards/types';
import { useReactRouterHistoryContext } from '../../v2/context/contexts';
import { styled } from '../styled';

export interface NavBarItemObj {
  id: string;
  name: string;
  link: string;
  subItems?: any[];
  disabled?: boolean;
  className?: string;
}

interface NavBarItemProps {
  toggleMenu: <T>(e: React.MouseEvent<T>) => void;
  closeMenu: <T>(e: React.MouseEvent<T>) => void;
  root?: boolean;
  level: number;
  color: string;
  name: string;
  subItems?: NavBarItemObj[];
  openByDefault?: boolean;
  className?: string;
  icon?: any;
}

// Replace StyledLink with a styled button for navigation
const MenuItemButton = styled.button`
  text-decoration: none;
  color: unset;
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
`;

const StyledSubItem = styled('div' as any)`
  position: relative;
  & > * > svg {
    width: 0.875em;
    height: 0.875em;
  }
  & > [role='tooltip'] {
    top: 0;
    left: 100%;
    z-index: 10;
  }
`;

const StyledList = styled(List)`
  max-height: 50vh;
  overflow: auto;
`;

const StyledBetaTypography = styled(Typography)`
  background-color: ${teal2};
  border-radius: 50%;
  width: 1.5em;
  height: 1.5em;
  margin-left: 0.5em;
  color: white;
  font-weight: 600;
  text-align: center;
`;

const NavBarItem = memo((props: NavBarItemProps) => {
  const {
    name,
    subItems,
    root,
    className,
    icon,
    toggleMenu,
    closeMenu,
    level,
    color,
  } = props;
  const [open, setOpen] = useState(props.openByDefault || false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeId, setActiveId] = useState<string>('');
  const history = useReactRouterHistoryContext();

  // Handle navigation with delay
  const handleNavigation = useCallback(
    (link: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset the anchorEl to close any open popper submenus
      setAnchorEl(null);
      setActiveId('');

      // Force close any open popper menus by clicking the body
      document.body.click();

      // Use startTransition to batch state updates
      startTransition(() => {
        // Close the menu
        closeMenu(e);

        // Then navigate immediately after
        history.push(link);
      });
    },
    [closeMenu, history]
  );

  const setActiveEl = useCallback(
    (id: string) => (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setAnchorEl(e.currentTarget);
      setActiveId(id);
    },
    []
  );

  const removeActiveEl = useCallback(
    () => (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setAnchorEl(null);
      setActiveId('');
    },
    []
  );

  const handleClick = useCallback((e: ClickEvent) => {
    setOpen((prev) => !prev);
  }, []);

  const renderMenu = () => {
    const activeItem = activeId
      ? subItems?.find((item) => item.id === activeId)
      : null;
    return (
      <Popper
        key={`popper-${activeId}`}
        open={!!anchorEl}
        style={{ zIndex: 2000 }}
        anchorEl={anchorEl}
        placement="right-start"
        container={document.querySelector('#tooltips-container')}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={removeActiveEl}>
                <List component={'div' as 'ul'}>
                  {activeItem?.subItems?.map((sI) => (
                    <MenuItemButton
                      key={sI.name}
                      onClick={(e) => handleNavigation(sI.link, e)}
                    >
                      <ListItemButton
                        key={sI.name}
                        role={undefined}
                        dense={true}
                      >
                        <ListItemText
                          primary={
                            <Grid
                              container
                              direction="row"
                              sx={{
                                alignItems: 'baseline',
                              }}
                            >
                              <Typography noWrap>
                                <Trans i18nKey={sI.name} />
                              </Typography>
                              {dashboardConfigurations.isBetaDashboard(
                                activeItem.id as Dashboards
                              ) ? (
                                <StyledBetaTypography>B</StyledBetaTypography>
                              ) : null}
                            </Grid>
                          }
                        />
                      </ListItemButton>
                    </MenuItemButton>
                  ))}
                </List>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    );
  };

  const renderListItems = (item: NavBarItemObj) => {
    const title = <Trans i18nKey="common:navigation.disabled" />;
    if (item.disabled) {
      return (
        <Tooltip title={title}>
          <StyledSubItem key={item.name}>
            <MenuItemButton
              disabled={item.disabled}
              onClick={(e) => handleNavigation(item.link, e)}
            >
              <ListItemButton
                disabled={item.disabled}
                role={undefined}
                dense={true}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Typography
                      style={{ color: '#ffffff' }}
                      className={item.className}
                    >
                      <Trans i18nKey={item.name} />
                    </Typography>
                  }
                />
                <Info />
              </ListItemButton>
            </MenuItemButton>
          </StyledSubItem>
        </Tooltip>
      );
    }
    if (item.subItems && item.subItems.length) {
      return (
        <StyledSubItem key={item.name} onMouseEnter={setActiveEl(item.id)}>
          <MenuItemButton onClick={(e) => handleNavigation(item.link, e)}>
            <ListItemButton
              disabled={item.disabled}
              role={undefined}
              dense={true}
            >
              <ListItemText
                disableTypography
                primary={
                  <Typography
                    style={{ color: '#ffffff' }}
                    className={item.className}
                  >
                    <Trans i18nKey={item.name} />
                  </Typography>
                }
              />
              {item.disabled && (
                <span>
                  <Tooltip title="Disabled">
                    <Info />
                  </Tooltip>
                </span>
              )}
              {!item.disabled &&
                (activeId === item.id ? <ChevronLeft /> : <ChevronRight />)}
            </ListItemButton>
          </MenuItemButton>
        </StyledSubItem>
      );
    }
    return (
      <MenuItemButton
        key={item.id}
        onClick={(e) => handleNavigation(item.link, e)}
      >
        <NavBarItem
          toggleMenu={toggleMenu}
          closeMenu={closeMenu}
          level={level + 1}
          key={item.name}
          name={item.name}
          subItems={[]}
          color={color}
          className={item.className}
        />
      </MenuItemButton>
    );
  };

  return (
    <>
      <ListItemButton
        key={name}
        role={undefined}
        dense={!root}
        onClick={handleClick}
        className={className}
      >
        <ListItemText
          disableTypography
          primary={
            <Typography style={{ color: '#ffffff' }}>
              {' '}
              <Trans i18nKey={name} />{' '}
            </Typography>
          }
        />
        {icon && (
          <ListItemSecondaryAction>
            <IconButton color="secondary" size="large">
              {icon}
            </IconButton>
          </ListItemSecondaryAction>
        )}
        {subItems &&
          subItems.length > 0 &&
          (open ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
      {subItems && (
        <Collapse in={open} timeout="auto">
          <div onMouseLeave={removeActiveEl()}>
            {subItems.length > 0 && (
              <StyledList>
                {subItems.map((item) => renderListItems(item))}
              </StyledList>
            )}
            {renderMenu()}
          </div>
        </Collapse>
      )}
    </>
  );
});

// Add display name for debugging
NavBarItem.displayName = 'NavBarItem';

export default NavBarItem;
